import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Clock,
  MapPin,
  DollarSign,
  Users,
  MessageSquare,
  Eye,
  Building,
  AlertCircle,
  CheckCircle2,
  ArrowRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminProjectApprovalListV2() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/admin/pending-projects`);
      setProjects(res.data || []);
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    if (status === "negotiating") {
      return {
        color: "bg-blue-500/20 text-blue-400 border-blue-500/30",
        label: "Negotiating",
        icon: MessageSquare
      };
    }
    return {
      color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      label: "Pending Approval",
      icon: Clock
    };
  };

  return (
    <DashboardLayout navItems={adminNavItems} title="Project Approval">
      <div className="space-y-6" data-testid="admin-approval-list">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-['Unbounded'] font-bold text-white">Project Approval</h1>
          <p className="text-[#94A3B8] mt-1">Review and approve client project submissions</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-400">
                  {projects.filter(p => p.status === "pending_approval").length}
                </p>
                <p className="text-sm text-[#94A3B8]">Pending Approval</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-400">
                  {projects.filter(p => p.status === "negotiating").length}
                </p>
                <p className="text-sm text-[#94A3B8]">In Negotiation</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-400">
                  {projects.reduce((acc, p) => acc + (p.unread_messages || 0), 0)}
                </p>
                <p className="text-sm text-[#94A3B8]">Unread Messages</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Projects List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="py-12 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h3 className="text-white text-lg font-semibold mb-2">All Caught Up!</h3>
              <p className="text-[#94A3B8]">No projects pending approval at the moment.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {projects.map((project) => {
              const statusConfig = getStatusConfig(project.status);
              const StatusIcon = statusConfig.icon;
              
              return (
                <Card 
                  key={project.project_id}
                  className="bg-[#0A0A0A] border-white/10 hover:border-[#23F7DD]/30 transition-all cursor-pointer group"
                  onClick={() => navigate(`/admin/project-approval/${project.project_id}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      {/* Project Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-white font-semibold text-lg truncate group-hover:text-[#23F7DD] transition-colors">
                            {project.title}
                          </h3>
                          <Badge className={statusConfig.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                          {project.unread_messages > 0 && (
                            <Badge className="bg-red-500/20 text-red-400">
                              {project.unread_messages} new
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-[#94A3B8]">
                          <span className="flex items-center gap-1">
                            <Building className="w-4 h-4" />
                            {project.client_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {project.location_city}
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            {project.proposed_budget?.toLocaleString()} NOK
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {new Date(project.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      {/* Action */}
                      <Button
                        className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/project-approval/${project.project_id}`);
                        }}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Review
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
